import type * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Label } from "@/v2/ui/primitives/label";
import { Switch } from "@/v2/ui/primitives/switch";

export interface FormSwitchProps {
  name: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export const FormSwitch: React.FC<FormSwitchProps> = ({
  name,
  label,
  description,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage = error?.message as string | undefined;

  return (
    <div
      className={cn("tw-space-y-1", className)}
      data-testid={TEST_IDS.formField(name)}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <div className="tw-flex-1">
          {label && (
            <Label htmlFor={name} className="tw-cursor-pointer">
              {label}
            </Label>
          )}
          {description && (
            <p className="tw-text-sm tw-text-muted-foreground tw-mt-1">
              {description}
            </p>
          )}
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Switch
              id={name}
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
      </div>
      {errorMessage && (
        <p
          className="tw-text-sm tw-text-destructive tw-mt-1"
          data-testid={TEST_IDS.formError(name)}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
